import { LineKeySpec, SpecMismatchView, SupplierView } from '../../components/discovery/discoveryModel';

/**
 * /design fixtures for spec chips on Supplier Discovery, shaped as the
 * backend's read delivers them (listingId, supplierKey, specGroup,
 * specMismatches against the line's current wording, specRank as `rank`).
 * The LiPo line is stage RFQ-134: 30C matches only The Engineer Store; 40C
 * and 35C are alternatives. The ground station is stage RFQ-135: one shop,
 * two matching products.
 */

const base = (name: string, extra: Partial<SupplierView>): SupplierView => ({
  name,
  advantages: [],
  risks: [],
  currencySymbol: '₹',
  hasContactEmail: true,
  ...extra,
});

const rate = (requested: string, offered: string, kind: SpecMismatchView['kind']): SpecMismatchView[] => [
  { key: 'dischargeRate', label: 'Discharge rate', requested, offered, kind },
];

const pack = (offered: string) => [{ requirement: '3S 11.1V 5200mAh 30C', offered: `3S 11.1V 5200mAh ${offered}`, met: 'met' as const }];

const ENGINEER_30C = base('The Engineer Store', {
  listingId: 'l_tes30', supplierKey: 's_tes', location: 'Mumbai, IN', trustScore: 80, belowTrustBar: true, quotedPrice: 4318.86,
  leadTimeWeeks: 1, websiteUrl: 'https://theengineerstore.in/products/lipo-3s-5200-30c', websiteKind: 'product', specMatch: pack('30C'),
  reason: 'The only seller listing the 30C pack you asked for.',
});
const ROBOCRAZE_40C = base('Robocraze (Orange 40C)', {
  listingId: 'l_rc40', supplierKey: 's_robocraze', location: 'Bengaluru, IN', trustScore: 88, quotedPrice: 3705, leadTimeWeeks: 1,
  websiteUrl: 'https://robocraze.com/products/orange-3s-5200mah-40c', websiteKind: 'product', specMatch: pack('40C'),
  reason: 'Ships from Bengaluru in a week, and clears your trust bar.',
});
const ROBU_40C = base('Robu.in (Robosync)', {
  listingId: 'l_robu40', supplierKey: 's_robu', location: 'Pune, IN', trustScore: 85, quotedPrice: 3838, leadTimeWeeks: 1,
  websiteUrl: 'https://robu.in/product/robosync-3s-5200mah-40c', websiteKind: 'product', specMatch: pack('40C'),
});
const HITECH_40C = base('Hi Tech xyz', {
  listingId: 'l_ht40', supplierKey: 's_hitech', location: 'Delhi, IN', trustScore: 65, belowTrustBar: true, quotedPrice: 3699,
  websiteUrl: 'https://hitechxyz.com/lipo-3s-5200-40c', websiteKind: 'product', specMatch: pack('40C'),
});
const ROBOCRAZE_35C = base('Robocraze', {
  listingId: 'l_rc35', supplierKey: 's_robocraze', location: 'Bengaluru, IN', trustScore: 90, quotedPrice: 4522,
  websiteUrl: 'https://robocraze.com/products/orange-3s-5200mah-35c', websiteKind: 'product', specMatch: pack('35C'),
});
const ECOMP_35C = base('Electronicscomp', {
  listingId: 'l_ec35', supplierKey: 's_ecomp', location: 'Bengaluru, IN', trustScore: 82, belowTrustBar: true, quotedPrice: 3799,
  websiteUrl: 'https://electronicscomp.com/lipo-3s-5200mah-35c', websiteKind: 'product', specMatch: pack('35C'),
});

const keySpecs = (rating: string): LineKeySpec[] => [
  { key: 'cells', label: 'Cell count', text: '3S' },
  { key: 'voltage', label: 'Voltage', text: '11.1V' },
  { key: 'capacity', label: 'Capacity', text: '5200mAh' },
  { key: 'dischargeRate', label: 'Discharge rate', text: rating },
];

export const LIPO_LINE = {
  name: 'LiPo battery',
  quantity: 1,
  description: '3S 11.1V 5200mAh 30C, XT60 discharge, JST-XH balance',
  keySpecs: keySpecs('30C'),
};

/** The same line after "Use 40C for this line": its wording now says 40C. */
export const LIPO_LINE_40C = {
  ...LIPO_LINE,
  description: '3S 11.1V 5200mAh 40C, XT60 discharge, JST-XH balance',
  keySpecs: keySpecs('40C'),
};

/** RFQ-134 as asked: 30C has one supplier; 40C and 35C are safe higher ratings. Ranks per spec. */
export const LIPO_30C: SupplierView[] = [
  { ...ENGINEER_30C, specGroup: '', specMismatches: [], rank: 1 },
  { ...ROBOCRAZE_40C, specGroup: 'dischargeRate=40C', specMismatches: rate('30C', '40C', 'exceeds'), rank: 1 },
  { ...ROBU_40C, specGroup: 'dischargeRate=40C', specMismatches: rate('30C', '40C', 'exceeds'), rank: 2 },
  { ...HITECH_40C, specGroup: 'dischargeRate=40C', specMismatches: rate('30C', '40C', 'exceeds'), rank: 3 },
  { ...ROBOCRAZE_35C, specGroup: 'dischargeRate=35C', specMismatches: rate('30C', '35C', 'exceeds'), rank: 1 },
  { ...ECOMP_35C, specGroup: 'dischargeRate=35C', specMismatches: rate('30C', '35C', 'exceeds'), rank: 2 },
];

/** After the switch: 40C is the line's spec, 30C and 35C are now lower ratings. */
export const LIPO_40C: SupplierView[] = [
  { ...ROBOCRAZE_40C, specGroup: '', specMismatches: [], rank: 1 },
  { ...ROBU_40C, specGroup: '', specMismatches: [], rank: 2 },
  { ...HITECH_40C, specGroup: '', specMismatches: [], rank: 3 },
  { ...ENGINEER_30C, specGroup: 'dischargeRate=30C', specMismatches: rate('40C', '30C', 'below'), rank: 1 },
  { ...ROBOCRAZE_35C, specGroup: 'dischargeRate=35C', specMismatches: rate('40C', '35C', 'below'), rank: 1 },
  { ...ECOMP_35C, specGroup: 'dischargeRate=35C', specMismatches: rate('40C', '35C', 'below'), rank: 2 },
];

export const GROUND_STATION_LINE = {
  name: 'Handheld ground station, 2.4 GHz, 7-inch display',
  quantity: 2,
  description: 'Handheld GCS with integrated 2.4 GHz radio and 7-inch sunlight-readable display',
  keySpecs: [{ key: 'frequency', label: 'Frequency', text: '2.4GHz' }],
};

const meridian = (listingId: string, offered: string, quotedPrice: number, extra: Partial<SupplierView> = {}) =>
  base('Meridian Technologies', {
    listingId, supplierKey: 's_meridian', location: 'Hyderabad, IN', trustScore: 87, quotedPrice, leadTimeWeeks: 3,
    websiteUrl: `https://meridiantech.in/products/${listingId}`, websiteKind: 'product', specGroup: '', specMismatches: [],
    specMatch: [{ requirement: 'Handheld GCS, 2.4 GHz, 7-inch', offered, met: 'met' }],
    ...extra,
  });

/** RFQ-135: Meridian sells two matching handhelds — one card, a tick per product. */
export const GROUND_STATION: SupplierView[] = [
  meridian('mt-h7', 'MT-H7 handheld GCS, 2.4 GHz, 7-inch', 84500, {
    rank: 1,
    reason: 'Two matching handhelds in stock; the H7 is the cheaper of the two.',
    negotiableTo: { price: 81000, basis: 'their 5-unit price' },
  }),
  meridian('mt-h7-pro', 'MT-H7 Pro handheld GCS, 2.4 GHz, 7-inch, IP65', 96200, { rank: 3 }),
  base('Skyfront Systems', {
    listingId: 'sf-gcs7', supplierKey: 's_skyfront', location: 'Bengaluru, IN', trustScore: 86, quotedPrice: 91800, rank: 2, leadTimeWeeks: 4,
    reason: 'Local stock; integrates with your telemetry radio.', specGroup: '', specMismatches: [],
    websiteUrl: 'https://skyfront.in/gcs-7', websiteKind: 'product',
  }),
];

/** A line where every listing matches the spec: no chips, the page as it always was. */
export const ESC_LINE = { name: 'ESC 30A, 2–4S', quantity: 4, description: '30A BLHeli_S ESC', keySpecs: [{ key: 'current', label: 'Current', text: '30A' }] };

export const ESC_SUPPLIERS: SupplierView[] = [
  base('Robu.in', {
    listingId: 'esc-robu', supplierKey: 's_robu', location: 'Pune, IN', trustScore: 88, quotedPrice: 899, rank: 1, leadTimeWeeks: 1,
    reason: 'Local stock, ships next day.', specGroup: '', specMismatches: [],
  }),
  base('Probots', {
    listingId: 'esc-probots', supplierKey: 's_probots', location: 'Bengaluru, IN', trustScore: 86, quotedPrice: 949, rank: 2, leadTimeWeeks: 1,
    specGroup: '', specMismatches: [],
  }),
  base('Evelta', {
    listingId: 'esc-evelta', supplierKey: 's_evelta', location: 'Mumbai, IN', trustScore: 79, quotedPrice: 870, rank: 3,
    specGroup: '', specMismatches: [],
  }),
];
